import { accountApi } from '../accountApi';
import { WriteOffCandidatesRequest } from '../requests/writeOffCandidatesRequest';
import { AccountView } from '../types/accountTypes';

/**
 * Builds the write-off candidates query string. The grid and the CSV export share it, so the
 * export always selects the same accounts the grid shows.
 */
const buildWriteOffCandidatesQuery = ({
  centerId,
  minimumAgeDays,
  minimumBalance,
  serviceDateFrom,
  serviceDateTo,
  payerGroups,
}: WriteOffCandidatesRequest): string => {
  const searchParams = new URLSearchParams({
    centerId,
    minimumAgeDays: String(minimumAgeDays ?? 90),
    minimumBalance: String(minimumBalance ?? 0.01),
  });
  if (serviceDateFrom) searchParams.append('serviceDateFrom', serviceDateFrom);
  if (serviceDateTo) searchParams.append('serviceDateTo', serviceDateTo);
  // Each payer group is a separate param for ASP.NET Core model binding
  payerGroups?.forEach((group) => searchParams.append('payerGroups', group));

  return searchParams.toString();
};

export const accountEndpoints = accountApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get write-off candidates for a center
    // GET /writeoff-candidates?centerId=<centerId>&...
    getWriteOffCandidates: builder.query<AccountView[], WriteOffCandidatesRequest>({
      query: (request) => ({
        url: `writeoff-candidates?${buildWriteOffCandidatesQuery(request)}`,
      }),
      providesTags: (result, error, { centerId }) => [
        { type: 'WriteOffCandidates', id: centerId },
        'AccountList',
      ],
    }),

    // Get account by ID
    // GET /account?id=<id>
    getAccountById: builder.query<AccountView, string>({
      query: (accountId) => ({
        url: 'account',
        params: { id: accountId },
      }),
      providesTags: (result, error, accountId) => [{ type: 'Account', id: accountId }],
    }),

    // Download write-off candidates as CSV file
    // GET /writeoff-candidates/download?centerId=<centerId>&...
    downloadWriteOffCandidates: builder.mutation<Blob, WriteOffCandidatesRequest>({
      query: (request) => ({
        url: `writeoff-candidates/download?${buildWriteOffCandidatesQuery(request)}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
    // The standard adjustment reasons, compiled into Accounts. Read only.
    getStandardReasons: builder.query<string[], void>({
      query: () => ({ url: '/adjustment-reasons', method: 'GET' }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetWriteOffCandidatesQuery,
  useLazyGetWriteOffCandidatesQuery,
  useGetAccountByIdQuery,
  useDownloadWriteOffCandidatesMutation,
  useGetStandardReasonsQuery,
} = accountEndpoints;
